import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import './FeaturesSettings.css';
import {
  LUCIDE_SERVICE_ICON_OPTIONS,
  normalizeServiceIconKey,
} from '../../constants/lucideServiceSectionIcons';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const scaleOption =
  LUCIDE_SERVICE_ICON_OPTIONS.find((o) => o.value === 'scale') ||
  LUCIDE_SERVICE_ICON_OPTIONS.find((o) => o.value === 'briefcase');

function selectedFeatureIconOption(iconKey) {
  const k = normalizeServiceIconKey(iconKey);
  return (
    LUCIDE_SERVICE_ICON_OPTIONS.find((o) => o.value === k) || scaleOption
  );
}

const iconSelectStyles = {
  control: (base) => ({
    ...base,
    backgroundColor: 'rgba(1, 7, 39, 0.8)',
    borderColor: 'rgba(255, 255, 255, 0.25)',
    minHeight: 38,
    boxShadow: 'none',
    '&:hover': { borderColor: 'rgba(255, 255, 255, 0.35)' },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#051139',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    zIndex: 20,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menuList: (base) => ({ ...base, maxHeight: 280, padding: 4 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? 'rgba(255, 255, 255, 0.12)'
      : 'transparent',
    color: '#ffffff',
    cursor: 'pointer',
    fontSize: 13,
  }),
  singleValue: (base) => ({ ...base, color: '#ffffff' }),
  input: (base) => ({ ...base, color: '#ffffff' }),
  placeholder: (base) => ({ ...base, color: 'rgba(246, 246, 246, 0.55)' }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: 'rgba(246, 246, 246, 0.75)',
  }),
};

const emptyFeature = () => ({
  id: Date.now().toString(),
  title: '',
  desc: '',
  iconKey: 'scale',
});

function FeaturesSettings() {
  const [features, setFeatures] = useState([]);
  const [stats, setStats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [featuresRes, statsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/features`),
          axios.get(`${API_BASE_URL}/api/feature-stats`),
        ]);

        if (Array.isArray(featuresRes.data?.features)) {
          setFeatures(featuresRes.data.features);
        } else {
          setFeatures([]);
        }

        if (Array.isArray(statsRes.data?.stats)) {
          setStats(statsRes.data.stats);
        } else {
          setStats([]);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAll();
  }, []);

  const handleChange = (index, field, value) => {
    setFeatures((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleDelete = (index) => {
    setFeatures((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    setFeatures((prev) => [...prev, emptyFeature()]);
  };

  const handleStatChange = (index, field, value) => {
    setStats((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleStatDelete = (index) => {
    setStats((prev) => prev.filter((_, i) => i !== index));
  };

  const handleStatAdd = () => {
    setStats((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        title: '',
        description: '',
        imageUrl: '',
        overlayType: 'blue',
      },
    ]);
  };

  const handleStatImageUpload = async (index, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/feature-stats/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );

      if (response.data?.url) {
        handleStatChange(index, 'imageUrl', response.data.url);
        setMessage('Image uploaded successfully.');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setMessage('Unable to upload image. Please try again.');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');

    try {
      const normalizedFeatures = features.map((f) => ({
        ...f,
        iconKey: normalizeServiceIconKey(f.iconKey),
      }));
      const [featuresRes, statsRes] = await Promise.all([
        axios.put(`${API_BASE_URL}/api/features`, normalizedFeatures),
        axios.put(`${API_BASE_URL}/api/feature-stats`, stats),
      ]);

      if (Array.isArray(featuresRes.data?.features)) {
        setFeatures(featuresRes.data.features);
      }
      if (Array.isArray(statsRes.data?.stats)) {
        setStats(statsRes.data.stats);
      }
      setMessage('Features and stats updated successfully.');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setMessage('Unable to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <p className="features-settings-loading">Loading features…</p>;
  }

  return (
    <div className="features-settings">
      <div className="features-settings-header">
        <div>
          <h2 className="features-settings-title">Features grid</h2>
          <p className="features-settings-subtitle">
            Manage the cards in the “Why choose us” features row on the home page.
          </p>
        </div>
        <div className="features-settings-actions">
          <button
            type="button"
            className="features-settings-secondary-btn"
            onClick={handleAdd}
          >
            + Add feature
          </button>
          <button
            type="button"
            className="features-settings-primary-btn"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className="features-settings-list">
        {features.map((feature, index) => (
          <div key={feature.id || index} className="features-settings-row">
            <div className="features-settings-col features-settings-col-main">
              <label className="features-settings-label">
                Title
                <input
                  type="text"
                  className="features-settings-input"
                  value={feature.title}
                  onChange={(e) =>
                    handleChange(index, 'title', e.target.value)
                  }
                  placeholder="Regulatory Strategy"
                />
              </label>

              <label className="features-settings-label">
                Description
                <textarea
                  className="features-settings-textarea"
                  rows={2}
                  value={feature.desc}
                  onChange={(e) =>
                    handleChange(index, 'desc', e.target.value)
                  }
                  placeholder="Short explanation that appears under the icon."
                />
              </label>
            </div>

            <div className="features-settings-col features-settings-col-icon">
              <label className="features-settings-label">
                Icon
                <Select
                  className="features-settings-icon-select"
                  classNamePrefix="features-settings-icon-select"
                  options={LUCIDE_SERVICE_ICON_OPTIONS}
                  value={selectedFeatureIconOption(feature.iconKey)}
                  onChange={(opt) =>
                    handleChange(index, 'iconKey', opt?.value || 'scale')
                  }
                  isSearchable
                  placeholder="Search icons…"
                  menuPlacement="auto"
                  menuPortalTarget={
                    typeof document !== 'undefined' ? document.body : null
                  }
                  styles={iconSelectStyles}
                  formatOptionLabel={(opt) => (
                    <span title={opt.value}>
                      {opt.label}{' '}
                      <span className="features-settings-icon-slug">
                        ({opt.value})
                      </span>
                    </span>
                  )}
                />
              </label>

              <button
                type="button"
                className="features-settings-delete-btn"
                onClick={() => handleDelete(index)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}

        {features.length === 0 && (
          <p className="features-settings-empty">
            No features configured yet. Click “Add feature” to create your first
            card.
          </p>
        )}
      </div>

      <div className="features-settings-header" style={{ marginTop: 24 }}>
        <div>
          <h3 className="features-settings-title">Feature stats cards</h3>
          <p className="features-settings-subtitle">
            Manage the three large statistic cards below the features grid, including
            their background images.
          </p>
        </div>
        <div className="features-settings-actions">
          <button
            type="button"
            className="features-settings-secondary-btn"
            onClick={handleStatAdd}
          >
            + Add stat card
          </button>
        </div>
      </div>

      <div className="features-settings-list">
        {stats.map((stat, index) => (
          <div key={stat.id || index} className="features-settings-row">
            <div className="features-settings-col features-settings-col-main">
              <label className="features-settings-label">
                Title
                <input
                  type="text"
                  className="features-settings-input"
                  value={stat.title}
                  onChange={(e) =>
                    handleStatChange(index, 'title', e.target.value)
                  }
                  placeholder="Digital Platform Services"
                />
              </label>

              <label className="features-settings-label">
                Description
                <textarea
                  className="features-settings-textarea"
                  rows={2}
                  value={stat.description}
                  onChange={(e) =>
                    handleStatChange(index, 'description', e.target.value)
                  }
                  placeholder="Short text displayed inside the stat card."
                />
              </label>
            </div>

            <div className="features-settings-col features-settings-col-icon">
              <label className="features-settings-label">
                Background image URL
                <input
                  type="text"
                  className="features-settings-input"
                  value={stat.imageUrl}
                  onChange={(e) =>
                    handleStatChange(index, 'imageUrl', e.target.value)
                  }
                  placeholder="https://example.com/image.jpg"
                />
              </label>

              <label className="features-settings-label">
                Or upload image
                <input
                  type="file"
                  accept="image/*"
                  className="features-settings-input"
                  onChange={(e) =>
                    handleStatImageUpload(index, e.target.files?.[0] || null)
                  }
                />
              </label>

              <label className="features-settings-label">
                Overlay style
                <select
                  className="features-settings-input"
                  value={stat.overlayType || 'blue'}
                  onChange={(e) =>
                    handleStatChange(index, 'overlayType', e.target.value)
                  }
                >
                  <option value="blue">Blue angle</option>
                  <option value="purple">Purple angle</option>
                </select>
              </label>

              <button
                type="button"
                className="features-settings-delete-btn"
                onClick={() => handleStatDelete(index)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}

        {stats.length === 0 && (
          <p className="features-settings-empty">
            No stat cards configured yet. Click “Add stat card” to create one.
          </p>
        )}
      </div>

      {message && <p className="features-settings-message">{message}</p>}
    </div>
  );
}

export default FeaturesSettings;

